

import React, { useMemo } from 'react';
import { User2, Settings, Info, ChevronLeft, ChevronRight } from 'lucide-react';
import { useConciergeStore } from '../../store/conciergeStore';

/**
 * SidePanel
 * A lightweight, right-hand sidebar used in the Concierge Center.
 * It shows simple tab buttons (Profile, Settings, About) and renders a panel area.
 *
 * Props:
 * - activeTab: string ('profile' | 'settings' | 'about')
 * - onSelect: (tab: string) => void
 * - collapsible: boolean (default true)
 * - collapsed: boolean (optional external control)
 * - onToggleCollapse: () => void (optional)
 */
export default function SidePanel({
  activeTab = 'profile',
  onSelect = () => {},
  collapsible = true,
  collapsed: collapsedProp,
  onToggleCollapse,
}) {
  const { activeThread } = useConciergeStore();

  // guest info to show on "Profile" tab header
  const guestSummary = useMemo(() => {
    const t = activeThread || {};
    const display =
      t.guestName ||
      t.fullName ||
      t.name ||
      t.guestEmail ||
      t.email ||
      t.userUid ||
      'Guest';
    return {
      title: String(display || 'Guest'),
      subtitle:
        (t.guestEmail || t.email) ? String(t.guestEmail || t.email) : (t.userUid ? `UID: ${t.userUid}` : ''),
    };
  }, [activeThread]);

  // allow local collapse state if prop not controlled
  const [collapsedState, setCollapsedState] = React.useState(false);
  const collapsed = typeof collapsedProp === 'boolean' ? collapsedProp : collapsedState;
  const toggleCollapse = () => {
    if (typeof onToggleCollapse === 'function') onToggleCollapse();
    else setCollapsedState(v => !v);
  };

  const TabButton = ({ id, icon: Icon, label }) => {
    const selected = activeTab === id;
    return (
      <button
        onClick={() => onSelect(id)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          width: '100%',
          padding: '10px 12px',
          borderRadius: 8,
          border: '1px solid ' + (selected ? '#d1e3ff' : 'transparent'),
          background: selected ? '#eaf2ff' : 'transparent',
          color: '#111827',
          cursor: 'pointer',
        }}
      >
        <Icon size={18} />
        <span style={{ fontSize: 14, fontWeight: 600 }}>{label}</span>
      </button>
    );
  };

  return (
    <aside
      style={{
        width: collapsed ? 44 : 280,
        borderLeft: '1px solid #E5E7EB',
        background: '#fff',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12, borderBottom: '1px solid #F0F2F5' }}>
        {collapsible && (
          <button
            onClick={toggleCollapse}
            title={collapsed ? 'Expand' : 'Collapse'}
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              border: '1px solid #E5E7EB',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#fff',
              cursor: 'pointer',
            }}
          >
            {collapsed ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
          </button>
        )}
        {!collapsed && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{guestSummary.title}</div>
            {guestSummary.subtitle && (
              <div style={{ fontSize: 12, color: '#6B7280' }}>{guestSummary.subtitle}</div>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      {!collapsed && (
        <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <TabButton id="profile" icon={User2} label="Profile" />
          <TabButton id="settings" icon={Settings} label="Settings" />
          <TabButton id="about" icon={Info} label="About" />
        </div>
      )}

      {/* Content area */}
      <div style={{ flex: 1, overflow: 'auto', padding: collapsed ? 6 : 12 }}>
        {!collapsed && (
          <>
            {activeTab === 'profile' && (
              <div>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>Guest Profile</div>
                {/* Placeholder content – you can replace with your GuestProfileDrawer component */}
                <div style={{
                  border: '1px solid #E5E7EB',
                  borderRadius: 12,
                  padding: 12,
                  display: 'grid',
                  gridTemplateColumns: '110px 1fr',
                  gap: 8
                }}>
                  <div style={{ color: '#6B7280', fontSize: 12 }}>Name</div>
                  <div style={{ fontWeight: 600 }}>{guestSummary.title}</div>
                  <div style={{ color: '#6B7280', fontSize: 12 }}>Contact</div>
                  <div>{guestSummary.subtitle || '—'}</div>
                </div>
                <div style={{ marginTop: 12, fontSize: 12, color: '#6B7280' }}>
                  (Replace this block with &lt;GuestProfileDrawer /&gt; or a richer profile panel.)
                </div>
              </div>
            )}
            {activeTab === 'settings' && (
              <div>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>Room Settings (local)</div>
                <ul style={{ margin: 0, paddingLeft: 16, lineHeight: 1.9 }}>
                  <li>Mute thread notifications</li>
                  <li>Collapse media previews</li>
                  <li>Compact bubble density</li>
                </ul>
              </div>
            )}
            {activeTab === 'about' && (
              <div>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>About this chat</div>
                <p style={{ marginTop: 0, color: '#374151' }}>
                  Property/thread metadata can be shown here (reservation, last seen, device, etc.).
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </aside>
  );
}